/// <reference path="../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import { CreateAuthenticationProvider } from '../backend/AuthenticationProviderFactory'
import { IsAuthError } from '../backend/office-js/auth/AuthErrors';
import { TranscriptionRoutePathNames } from './routes';
import { IAuthenticationProvider } from '../backend/interfaces/IAuthenticationProvider';
import * as Logger from '../backend/TelemetryLogger/Details/Logger';
import { InitializeRuntimeManager } from '../backend/augloop/Augloop';
import { AugloopError, AugloopErrors } from '../backend/augloop/AugloopErrors';
import { MAX_BOOT_TIME_MILLISECONDS, AUTH_ENVIRONMENT_DEFAULT, IS_DEV_BUILD, DEV_AUGLOOP_URL } from '../AppConstants';
import { SetFailureStateAndRouteToFailure, FailureContinuation } from './actions/FailureActions';
import { InitializeAudioBackup, AudioBackupAndRecoveryStatus } from './actions/RecoveringActions';
import { ErrorStringFromException } from '../utils/utils';
import { NetworkError, NetworkErrors } from '../utils/Errors';
import * as LoggerFactory from '../backend/TelemetryLogger/Details/LoggerFactory';
import { LogEvents } from '../utils/LogEvents';
import { BootKeys } from './telemetry/BootKeys';
import { LogError, ShouldLogErrorAsWarning } from '../utils/ErrorTelemetry';
import { RenderToPaneKeys } from './pages/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../backend/interfaces/ILogEvent';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { NetworkConnectionMonitor, CheckNetworkConnection } from './NetworkConnectionMonitor';
import * as GlobalErrorMonitor from '../utils/GlobalErrorMonitor';
import { ResettableTimeout } from '../utils/ResettableTimeout';
import { initializeIcons } from 'office-ui-fabric-react';
import { HostAppSettings, HostAppSettingName } from '../backend/office-js/HostAppSettings';
import { LoadTranscriptionStringsAsync, LoadTranscriptionStringsError, LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from './LoadTranscriptionStrings';
import { BootError, BootErrors } from './BootErrors';
import { NavigateToLandingPageAfterAudioBackupCheck } from './actions/LandingActions';
import { AudioBackupError, AudioBackupErrors } from '../backend/audioBackup/AudioBackupErrors';

function GetFailureReasonForInitializeAppError(error) {
  // If we couldn't even load strings, show an English string.
  if (error instanceof LoadTranscriptionStringsError || (error instanceof BootError && error.name == BootErrors.InvalidLoadTranscriptionStringsPromise))
    return LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE;

  if (error instanceof BootError && error.name == BootErrors.IndexeddbFailedToOpen
    || error instanceof AudioBackupError && error.name == AudioBackupErrors.UnsupportedBrowser) {
    return TranscriptionStrings.CookieSettingsFailureReason;
  }

  if (error instanceof BootError && error.name == BootErrors.MaxBootTimeReached)
    return TranscriptionStrings.InitHangFailureReason;

  if (error instanceof AugloopError && error.name == AugloopErrors.SessionInitTimeout)
    return TranscriptionStrings.UnableToConnectToTranscriptionServiceFailureReason;

  if (IsAuthError(error)) {
    return TranscriptionStrings.UnableToAuthenticateNoBackupFailureReason;
  }

  if (error instanceof NetworkError && error.name == NetworkErrors.NoInternetConnection)
    return TranscriptionStrings.ConnectionIssueFailureReason;

  return TranscriptionStrings.UnknownInitFailureReason;
}


//-----------------------------------------------------------
//-------------------  ASYNC TASKS --------------------------
function InitializeBackupStateTask(history: any): Promise<any> {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeBackupStateDuration);

  const initializeBackupStateTask = InitializeAudioBackup().then(
    async (backupAndState: AudioBackupAndRecoveryStatus) => {
      LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeBackupStateDuration);
      return SetInitialPageFromBackupStateAndStoredTranscription(backupAndState, history);
    }
  );

  return initializeBackupStateTask;
}

let loadTranscriptionStringsTask: Promise<void> = null;
function LoadTranscriptionStringsTask(): Promise<any> {
    if (typeof TranscriptionStrings !== "undefined")
      return Promise.resolve();

    // Load transcription strings task is initialized outside of InitializeAsyncTasks because we need to await it if we fail anywhere during boot.
    LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.LoadTranscriptionStringsDuration);
    return LoadTranscriptionStringsAsync().then(() => { 
      LogEvents.Boot.EndDurationMeasure(BootKeys.LoadTranscriptionStringsDuration); 
    });
}

// Initialize any asynchronous tasks to be executed during boot.
// Returns a list of promises for such tasks.
function InitializeAsyncTasks(history: any): Promise<any>[] {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeAsyncTasksStageDuration);
  const asyncTasks: Promise<any>[] = [];

  loadTranscriptionStringsTask = LoadTranscriptionStringsTask();

  asyncTasks.push(loadTranscriptionStringsTask);
  asyncTasks.push(InitializeBackupStateTask(history));

  LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeAsyncTasksStageDuration);
  return asyncTasks;
}

// Wait for the asynchronous tasks executed during boot to finish
async function WaitForAsyncTasks(asyncTasks: Promise<any>[]) {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.WaitForAsyncTasksStageDuration);

  await Promise.all(asyncTasks);

  LogEvents.Boot.EndDurationMeasure(BootKeys.WaitForAsyncTasksStageDuration);
}

//------------------- END ASYNC TASKS -----------------------
//-----------------------------------------------------------

async function SetInitialPageFromBackupStateAndStoredTranscription(backupAndState: AudioBackupAndRecoveryStatus, history: any)
{
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.SetInitialPageFromBackupStateAndStoredTranscriptionDuration);
  if (backupAndState.needsRecovery) {
    history.replace(TranscriptionRoutePathNames.Recovering, {audioBackup: backupAndState.audioBackup});
  } else if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Pane loaded with preexisting transcription" }]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
    if (backupAndState.audioBackup != null) {
      backupAndState.audioBackup.Close();
      backupAndState.audioBackup = null;
    }
    history.replace(TranscriptionRoutePathNames.Loading);
  } else {
    NavigateToLandingPageAfterAudioBackupCheck(backupAndState, history);
  }
  LogEvents.Boot.EndDurationMeasure(BootKeys.SetInitialPageFromBackupStateAndStoredTranscriptionDuration);
}

function InitializeAugLoopRuntimeManager() {
  let augLoopCloudALServiceUrl: string = IS_DEV_BUILD ? DEV_AUGLOOP_URL : HostAppSettings.GetSettingAsString(HostAppSettingName.AugLoopCloudALServiceUrl);
  if (augLoopCloudALServiceUrl == null || augLoopCloudALServiceUrl == '') {
    throw new Error('augLoopCloudALServiceUrl not defined');
  }

  // HACK: If we are connecting to augloop dogfood, use int instead so we
  // connect to FrontDoor PPE in order to test NearRT. VSO:4376460 tracks removal.
  if (augLoopCloudALServiceUrl == "https://augloop-dogfood.officeppe.com") {
    augLoopCloudALServiceUrl = "https://augloop-int.officeppe.com"
  }

  InitializeRuntimeManager(augLoopCloudALServiceUrl);
}

// Execute any synchronous tasks required during boot.
function ExecuteSynchronousTasks(
  history: any, 
  setAuthProvider: (authProvider: IAuthenticationProvider) => void, 
  setIsAppLoaded: (isAppLoaded: boolean) => void)
    : ResettableTimeout {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.ExecuteSynchronousTasksStageDuration);

  CheckNetworkConnection();

  const timeoutAssumesHang: ResettableTimeout = new ResettableTimeout(() => {
    HandleError(new BootError(BootErrors.MaxBootTimeReached, "Reached max boot time of " + MAX_BOOT_TIME_MILLISECONDS), timeoutAssumesHang, setIsAppLoaded, history);
  }, MAX_BOOT_TIME_MILLISECONDS);

  initializeIcons();

  const networkMonitor = new NetworkConnectionMonitor(history);
  networkMonitor.Start();

  const authProvider: IAuthenticationProvider = CreateAuthenticationProvider(AUTH_ENVIRONMENT_DEFAULT);
  authProvider.Authenticate();
  setAuthProvider(authProvider);
  
  LogEvents.Boot.EndDurationMeasure(BootKeys.ExecuteSynchronousTasksStageDuration);
  return timeoutAssumesHang;
}

// Do any initialization required at the beginning of boot.
function InitializeBoot(): void {
    Logger.setLoggerInstance(LoggerFactory.createLoggerInstance(LoggerFactory.LoggerType.OTelBasedLogger));

    const transcriptionSessionId: string = HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId);
    LogEvents.Boot.Start([
      { key: BootKeys.TranscriptionSessionId, value: transcriptionSessionId }, 
      { key: BootKeys.TranscriptAssociated, value: HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription) },
      { key: BootKeys.IsFreemiumUpsellEnabled, value: HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled) },
      { key: BootKeys.IsLicensedUser, value: HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser) }
    ]);
    LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.DurationSeconds);
    LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeBootStageDuration);

    GlobalErrorMonitor.Start(LogEventNames.Boot);
    console.log('Transcription session ID: ' + transcriptionSessionId);

    // This is necessary here because InitializeBackupState async task could call into SetInitialPageFromBackupStateAndStoredTranscription before the later synchronous tasks are executed
    InitializeAugLoopRuntimeManager();

    LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeBootStageDuration);
}

// Do any final bookkeeping at the end of boot.
function FinalizeBoot(finishedSuccessfully: boolean, setIsAppLoaded: (isAppLoaded: boolean) => void, timeoutAssumesHang: ResettableTimeout) {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.FinalizeBootStageDuration);

  // Set app as loaded so we show the initial page in the next render.
  setIsAppLoaded(true);
  
  if (timeoutAssumesHang) {
    timeoutAssumesHang.Cancel();
    timeoutAssumesHang = null;
  }

  LogEvents.Boot.EndDurationMeasure(BootKeys.FinalizeBootStageDuration);

  LogEvents.Boot.End([{
    key: BootKeys.DidBootFinishSuccessfully,
    value: finishedSuccessfully
  }]);

  GlobalErrorMonitor.SetErrorArea(LogEventNames.Error);
}

// Boot is composed of the following sequential stages:
// 1. Initialize boot
// 2. Initialize async tasks
// 3. Execute synchronous tasks
// 4. Wait for async tasks to complete
// 5. Finalize boot
export async function InitializeApp(history: any, setAuthProvider: (authProvider: IAuthenticationProvider) => void, setIsAppLoaded: (isAppLoaded: boolean) => void) {
  let timeoutAssumesHang: ResettableTimeout = null;

  try {
    InitializeBoot();

    const asyncTasks: Promise<any>[] = InitializeAsyncTasks(history);

    timeoutAssumesHang = ExecuteSynchronousTasks(history, setAuthProvider, setIsAppLoaded);

    await WaitForAsyncTasks(asyncTasks);

    FinalizeBoot(true /*finishedSuccessfully*/, setIsAppLoaded, timeoutAssumesHang);
  } catch (error) {
    try {
      if (loadTranscriptionStringsTask == null) {
        throw new BootError(BootErrors.InvalidLoadTranscriptionStringsPromise, "Load transcription strings promise was null when handling error. Error being handled: " + ErrorStringFromException(error));
      }

      // Wait for strings to load before showing the error message.
      await loadTranscriptionStringsTask;
      HandleError(error, timeoutAssumesHang, setIsAppLoaded, history);
    } catch (loadStringsError) {
      // If we failed loading strings, just go through normal error handling which will handle LoadTranscriptionStringsError and BootErrors.InvalidLoadTranscriptionStringsPromise.
      HandleError(loadStringsError, timeoutAssumesHang, setIsAppLoaded, history);
    }
  }
}

function HandleError(e: any, timeoutAssumesHang: ResettableTimeout, setIsAppLoaded: (isAppLoaded: boolean) => void, history: any) {
  let failureArea = LogEventNames.Boot;
  if (ShouldLogErrorAsWarning(e)) {
    failureArea = LogEventNames.Warning;
  }

  LogError(failureArea, "Opening pane failed with error: " + ErrorStringFromException(e));
  
  FinalizeBoot(false /*finishedSuccessfully*/, setIsAppLoaded, timeoutAssumesHang);

  // Show Failure page
  SetFailureStateAndRouteToFailure(failureArea, GetFailureReasonForInitializeAppError(e), FailureContinuation.None /*failureContinuation*/, history);
}